import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { Gas } from "@domain/gas/gas";
import { FgdsProductType, LocalizeProductNamePipe, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FlameDetectorType } from "@project/flame-detector-configuration/flame-detector-type";
import { TransmitterCardComponent } from "@project/transmitter-configuration/transmitter-card/transmitter-card.component";
import { first, map } from "rxjs";

@Component({
  selector: "app-flame-detector-variant-selector",
  standalone: true,
  templateUrl: "./flame-detector-variant-selector.component.html",
  styleUrls: ["./flame-detector-variant-selector.component.scss"],
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    FilterProductComponent,
    RefreshProductsHintComponent,
    TransmitterCardComponent,
    LocalizeProductNamePipe,
  ],
  providers: [LocalizeProductNamePipe],
})
export class FlameDetectorVariantSelectorComponent implements OnChanges {
  private productService = inject(ProductService);
  private formBuilder = inject(FormBuilder);

  @Input() selectedFamily?: FlameDetectorType;
  @Input() selectedSubstance?: Gas;

  @Input() set originalVariant(product: Product | undefined) {
    this._originalVariant = product;
  }

  @Input() showCosts = false;

  @Output() variantSelect$ = new EventEmitter<Product>();

  protected selectedVariant?: Product;
  protected filteredSelectableProducts: Product[] = [];

  private selectableProducts: Product[] = [];
  private _originalVariant?: Product;

  protected selectedVariantForm: FormGroup;

  constructor() {
    this.selectedVariantForm = this.formBuilder.group({
      selectedVariant: [null],
    });

    this.selectedVariantForm.valueChanges.subscribe(() => {
      this.selectedVariant = this.selectedVariantForm.value.selectedVariant;
      this.variantSelect$.emit(this.selectedVariant);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const familyChanged = "selectedFamily" in changes;
    const substanceChanged = "selectedSubstance" in changes;

    if (familyChanged) {
      this.clearVariantSelection();
      this.clearProducts();
    }

    if ((familyChanged || substanceChanged) && this.selectedFamily && this.selectedSubstance) {
      const gasProductsById = new Map(this.selectedSubstance.products.map((gasProduct) => [gasProduct.productId, gasProduct]));
      const selectableProductIds = this.selectedSubstance.products.map((gasProduct) => gasProduct.productId);

      if (selectableProductIds.length) {
        this.productService
          .getProductsByIds(selectableProductIds, FgdsProductType.FLAME_DETECTOR)
          .pipe(
            map((products: Product[]) =>
              Product.sort(products).filter((product) => {
                if (!product.isAvailable) return false;

                const gasProduct = gasProductsById.get(product.id);
                // `rawName` is only a legacy fallback for gas data cached before parentFamilies existed.
                const familyNames = product.parentFamilies.length ? product.parentFamilies : [gasProduct?.rawName];
                return familyNames.some((familyName) => familyName?.toLowerCase() === this.selectedFamily!.name.toLowerCase());
              }),
            ),
            first(),
          )
          .subscribe((products: Product[]) => this.setProducts(products));
      }
    }
  }

  protected search(value: string) {
    this.filteredSelectableProducts = this.selectableProducts.filter((product) =>
      this.productService.searchFilter(product, value),
    );
  }

  protected select(variant: Product) {
    this.selectedVariantForm.patchValue({ selectedVariant: variant });
  }

  private clearProducts() {
    this.setProducts([]);
  }

  private setProducts(products: Product[]) {
    this.selectableProducts = products;
    this.filteredSelectableProducts = products;

    if (this._originalVariant?.isAvailable) {
      const match = products.find((p) => p.id === this._originalVariant!.id);
      if (match) {
        this.select(match);
      }
    }
  }

  private clearVariantSelection() {
    this.selectedVariant = undefined;
    this.selectedVariantForm?.patchValue({ selectedVariant: null }, { emitEvent: false });
  }
}
